import { useCallback, useState } from 'react';
import { createApiUrl } from '../../../config/api';
import { useApiAuth } from '../../../hooks/useApiAuth';
import type { QMSFieldData, QMSIntegrationResponseData } from '../../../types/dataResponse';
import type { ChatMessage } from '../../ChatBox/Messages';

interface CachedReport {
  id: string;
  timestamp: number;
  label: string;
  results: QMSIntegrationResponseData[];
}

interface QMSTabProps {
  sessionId: string;
  userId: string;
  tabId: string;
  qmsData?: {
    fields: QMSFieldData[];
    results?: QMSIntegrationResponseData[];
    report_history?: CachedReport[];
  };
  onResponse: (response: any) => void;
  onReportMessage?: (message: ChatMessage) => void;
}

// Editable result card component
function EditableResultCard({
  result,
  index,
  onContentChange
}: {
  result: QMSIntegrationResponseData;
  index: number;
  onContentChange: (index: number, content: string) => void;
}) {
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow group">
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between p-4 cursor-pointer min-w-0"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Expand/collapse arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="flex-shrink-0 w-5 h-5 hover:bg-gray-100 rounded flex items-center justify-center transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            <svg
              className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <h3 className="text-base font-medium text-gray-900 truncate flex-1 min-w-0">{result.name}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200"
            title="Copy to clipboard"
          >
            {isCopied ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className={`overflow-hidden transition-all duration-300 border-t border-gray-100 ${
          isExpanded ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="p-4 pt-3">
          <textarea
            value={result.content}
            onChange={(e) => onContentChange(index, e.target.value)}
            className="w-full min-h-[150px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-gray-600 text-sm leading-relaxed"
            placeholder="Edit content here..."
          />
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Edit the content above as needed</span>
            <span>{result.content.length} characters</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function QMSTab({ sessionId, tabId, qmsData, onResponse, onReportMessage }: QMSTabProps) {
  const { makeAuthenticatedRequest } = useApiAuth();
  const [fields, setFields] = useState<QMSFieldData[]>(qmsData?.fields || []);
  const [results, setResults] = useState<QMSIntegrationResponseData[]>(qmsData?.results || []);
  const [reportHistory, setReportHistory] = useState<CachedReport[]>(qmsData?.report_history || []);
  const [viewingCachedReportId, setViewingCachedReportId] = useState<string | null>(null); // Track if viewing cached report
  const [isGenerating, setIsGenerating] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [showAddField, setShowAddField] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [editFieldName, setEditFieldName] = useState('');
  const [editFieldDescription, setEditFieldDescription] = useState('');

  const selectedFieldsCount = fields.filter(f => f.is_selected).length;
  const isStructuredMode = selectedFieldsCount > 0;

  const toggleFieldSelection = (index: number) => {
    setFields(prevFields =>
      prevFields.map((field, i) =>
        i === index ? { ...field, is_selected: !field.is_selected } : field
      )
    );
  };

  const handleAddCustomField = () => {
    if (newFieldName.trim() && newFieldDescription.trim()) {
      setFields(prevFields => [
        ...prevFields,
        {
          name: newFieldName.trim(),
          description: newFieldDescription.trim(),
          is_selected: true,
        }
      ]);
      setNewFieldName('');
      setNewFieldDescription('');
      setShowAddField(false);
    }
  };

  const handleRemoveField = (index: number) => {
    setFields(prevFields => prevFields.filter((_, i) => i !== index));
  };

  const handleStartEdit = (index: number) => {
    setEditingFieldIndex(index);
    setEditFieldName(fields[index].name);
    setEditFieldDescription(fields[index].description);
  };

  const handleSaveEdit = () => {
    if (editingFieldIndex !== null && editFieldName.trim() && editFieldDescription.trim()) {
      setFields(prevFields =>
        prevFields.map((field, i) =>
          i === editingFieldIndex
            ? { ...field, name: editFieldName.trim(), description: editFieldDescription.trim() }
            : field
        )
      );
      setEditingFieldIndex(null);
      setEditFieldName('');
      setEditFieldDescription('');
    }
  };

  const handleCancelEdit = () => {
    setEditingFieldIndex(null);
    setEditFieldName('');
    setEditFieldDescription('');
  };

  const handleResultContentChange = (index: number, content: string) => {
    setResults(prevResults =>
      prevResults.map((r, i) => i === index ? { ...r, content } : r)
    );
  };

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    
    // Send initial message immediately when user clicks generate
    const messageId = `qms-${Date.now()}`;
    const messageCreatedAt = new Date(); // Capture timestamp once for consistent ordering
    if (onReportMessage) {
      onReportMessage({
        role: 'assistant',
        content: 'Generating quality investigation analysis...',
        id: messageId,
        createdAt: messageCreatedAt
      });
    }
    
    try {
      const response = await makeAuthenticatedRequest(
        createApiUrl(`/qms/generate_qms/session_id/${sessionId}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: fields,
            tab_reference_id: tabId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate QMS analysis');
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let apiResults: QMSIntegrationResponseData[] = [];

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim() && line.startsWith('2:[')) {
              try {
                const jsonStr = line.slice(2); // Remove '2:' prefix
                const data = JSON.parse(jsonStr);

                if (Array.isArray(data) && data.length > 0) {
                  const item = data[0];

                  if (item.type === 'qms' && item.data?.payload) {
                    // Parse the QMS results from the payload
                    const parsedResults = JSON.parse(item.data.payload);
                    apiResults = parsedResults;
                  } else if (item.type === 'plan') {
                    // Forward plan updates to the parent
                    onResponse([item]);
                  }
                }
              } catch (e) {
                console.error('Failed to parse streaming data:', e);
              }
            }
          }
        }
      }

      setResults(apiResults);
      setViewingCachedReportId(null); // This is a fresh generation, not viewing a cached report

      // Update the tab with results
      onResponse([{
        type: 'qms',
        tab_destination: 'qms',
        reference_id: tabId,
        data: {
          fields: fields,
          results: apiResults,
          report_history: reportHistory,
        }
      }]);

      // Send completion message AFTER updating the response
      if (onReportMessage) {
        onReportMessage({
          role: 'assistant',
          content: 'The quality investigation analysis has been completed successfully!',
          id: messageId,
          createdAt: messageCreatedAt
        });
      }
    } catch (error) {
      console.error('Error generating QMS analysis:', error);
      
      // Send error message
      if (onReportMessage) {
        onReportMessage({
          role: 'assistant',
          content: 'Failed to generate QMS analysis. Please try again.',
          id: messageId,
          createdAt: messageCreatedAt
        });
      }
      
      alert('Failed to generate QMS analysis. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [fields, sessionId, tabId, onResponse, onReportMessage, makeAuthenticatedRequest, reportHistory]);

  const handleBackToSelection = () => {
    // Only cache if this is a fresh generation (not viewing a cached report)
    if (results.length > 0 && viewingCachedReportId === null) {
      const newCachedReport: CachedReport = {
        id: `analysis-${Date.now()}`,
        timestamp: Date.now(),
        label: `QMS Analysis ${reportHistory.length + 1}`,
        results: [...results],
      };
      const updatedHistory = [...reportHistory, newCachedReport];
      setReportHistory(updatedHistory);

      // Clear current results and update parent with history
      setResults([]);
      setViewingCachedReportId(null);
      onResponse([{
        type: 'qms',
        tab_destination: 'qms',
        reference_id: tabId,
        data: {
          fields: fields,
          results: undefined,
          report_history: updatedHistory,
        }
      }]);
    } else {
      // Just going back without caching (viewing cached report or no results)
      setResults([]);
      setViewingCachedReportId(null);
      onResponse([{
        type: 'qms',
        tab_destination: 'qms',
        reference_id: tabId,
        data: {
          fields: fields,
          results: undefined,
          report_history: reportHistory,
        }
      }]);
    }
  };

  const handleViewCachedReport = (cachedReport: CachedReport) => {
    setResults(cachedReport.results);
    setViewingCachedReportId(cachedReport.id); // Mark that we're viewing a cached report
  };

  const handleDeleteCachedReport = (reportId: string) => {
    const updatedHistory = reportHistory.filter(r => r.id !== reportId);
    setReportHistory(updatedHistory);
    onResponse([{
      type: 'qms',
      tab_destination: 'qms',
      reference_id: tabId,
      data: {
        fields: fields,
        results: results.length > 0 ? results : undefined,
        report_history: updatedHistory,
      }
    }]);
  };

  // Show results view if we have results
  if (results.length > 0 || (qmsData?.results && qmsData.results.length > 0)) {
    const displayResults = results.length > 0 ? results : qmsData?.results || [];

    return (
      <div className="h-full w-full flex flex-col bg-gray-50">
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-800">Quality Investigation Results</h1>
                <p className="text-sm text-gray-500">Edit and copy the generated content</p>
              </div>
            </div>
            <button
              onClick={handleBackToSelection}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Selection</span>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="max-w-4xl mx-auto">

          {/* Results Cards */}
          <div className="space-y-3">
            {displayResults.map((result, index) => (
              <EditableResultCard
                key={index}
                result={result}
                index={index}
                onContentChange={handleResultContentChange}
              />
            ))}
          </div>

          {/* Action Bar */}
          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={async () => {
                const allContent = displayResults
                  .map(r => `## ${r.name}\n\n${r.content}`)
                  .join('\n\n---\n\n');
                try {
                  await navigator.clipboard.writeText(allContent);
                  alert('All content copied to clipboard!');
                } catch (err) {
                  console.error('Failed to copy:', err);
                }
              }}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy All to Clipboard
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // Show field selection interface
  return (
    <div className="h-full w-full flex flex-col bg-gray-50">
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">Tailor your quality investigation</h1>
            <p className="text-sm text-gray-500">Select sections for guided investigation, or generate comprehensive analysis</p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        <div className={`w-full mx-auto flex gap-4 ${reportHistory.length > 0 ? 'max-w-5xl' : 'max-w-3xl'}`}>
        {/* Main selection panel */}
        <div className={`bg-white rounded-lg border border-gray-200 shadow-sm p-6 ${reportHistory.length > 0 ? 'flex-1' : 'w-full'}`}>

        <div className="space-y-4">
          {/* QMS Sections */}
          <div className="space-y-2">
            {fields.map((field, index) => (
              editingFieldIndex === index ? (
                // Edit mode
                <div key={index} className="bg-blue-50 rounded-lg p-4 border-2 border-blue-500">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section Name</label>
                      <input
                        type="text"
                        value={editFieldName}
                        onChange={(e) => setEditFieldName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editFieldDescription}
                        onChange={(e) => setEditFieldDescription(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!editFieldName.trim() || !editFieldDescription.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Display mode
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer group ${
                    field.is_selected
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                  onClick={() => toggleFieldSelection(index)}
                >
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold truncate ${field.is_selected ? 'text-blue-800' : 'text-gray-800'}`}>
                      {field.name}
                    </h4>
                    <p className="text-sm text-gray-600 line-clamp-2">{field.description}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(index);
                      }}
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit section"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {/* Only show remove button for custom sections (beyond the predefined 6) */}
                    {index >= 6 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveField(index);
                        }}
                        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove section"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Add Custom Section */}
          <div className="border-t border-gray-200 pt-4">
            {!showAddField ? (
              <button
                onClick={() => setShowAddField(true)}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add custom section</span>
              </button>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Name</label>
                    <input
                      type="text"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      placeholder="e.g., Environmental Conditions"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={newFieldDescription}
                      onChange={(e) => setNewFieldDescription(e.target.value)}
                      placeholder="Describe what this section should analyze..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddCustomField}
                      disabled={!newFieldName.trim() || !newFieldDescription.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Section
                    </button>
                    <button
                      onClick={() => {
                        setShowAddField(false);
                        setNewFieldName('');
                        setNewFieldDescription('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={`w-full py-2.5 rounded-lg font-medium text-sm text-white transition-all shadow-sm ${
              isGenerating
                ? 'bg-gray-400 cursor-not-allowed'
                : isStructuredMode
                  ? 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                  : 'bg-gray-600 hover:bg-gray-700 hover:shadow-md'
            }`}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Analysis...
              </span>
            ) : isStructuredMode ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                GENERATE GUIDED INVESTIGATION
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                GENERATE COMPREHENSIVE INVESTIGATION
              </span>
            )}
          </button>

          {/* Helper text */}
          <p className="text-center text-sm text-gray-500">
            {isStructuredMode
              ? `${selectedFieldsCount} section${selectedFieldsCount !== 1 ? 's' : ''} selected for guided investigation`
              : 'No sections selected - will generate a comprehensive investigation'
            }
          </p>
        </div>
        </div>

        {/* Previous QMS Analyses Sidebar */}
        {reportHistory.length > 0 && (
          <div className="w-64 bg-white rounded-lg border border-gray-200 shadow-sm p-4 h-fit">
            <h3 className="text-base font-semibold text-gray-800 mb-3">Previous QMS Analyses</h3>
            <div className="space-y-2">
              {reportHistory.map((report) => (
                <div
                  key={report.id}
                  className="group relative flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                  onClick={() => handleViewCachedReport(report)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{report.label}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(report.timestamp).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">
                      {report.results.length} section{report.results.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCachedReport(report.id);
                    }}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                    title="Delete analysis"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
